// packages/web/src/components/ui/Field.tsx — misma API; label asociado, error accesible.
import { useId, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';

interface Common {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | null | undefined;
  required?: boolean | undefined;
}
type InputProps = Common & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>;
type TextareaProps = Common & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>;

export const INPUT_CLASS =
  'mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-marzano-500 focus:border-marzano-500 ' +
  'aria-[invalid=true]:border-red-600 aria-[invalid=true]:ring-red-200 ' +
  'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed';

function LabelBlock({ id, label, required, children, hint, error }: Common & { id: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800">
        {label}
        {required && <span className="text-red-700 ml-0.5" aria-hidden>*</span>}
      </label>
      {children}
      {error ? (
        <p id={`${id}-msg`} className="text-[13px] font-medium text-red-700 mt-1.5">{error}</p>
      ) : hint ? (
        <p id={`${id}-msg`} className="text-[13px] text-slate-600 mt-1.5">{hint}</p>
      ) : null}
    </div>
  );
}

export function Field({ label, hint, error, required, id, ...rest }: InputProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <LabelBlock id={fid} label={label} required={required} hint={hint} error={error}>
      <input {...rest} id={fid} required={required} aria-invalid={!!error || undefined}
        aria-describedby={error || hint ? `${fid}-msg` : undefined} className={INPUT_CLASS} />
    </LabelBlock>
  );
}

export function TextareaField({ label, hint, error, required, rows = 3, id, ...rest }: TextareaProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <LabelBlock id={fid} label={label} required={required} hint={hint} error={error}>
      <textarea {...rest} id={fid} rows={rows} required={required} aria-invalid={!!error || undefined}
        aria-describedby={error || hint ? `${fid}-msg` : undefined} className={INPUT_CLASS + ' resize-y'} />
    </LabelBlock>
  );
}
